import { theme, Typography } from 'antd';
import type { ReactNode } from 'react';
import type { IncomingSummaryMetal } from '../../domain/metals/incomingSummary';
import {
    formatStockNumber,
    GRAMS_DIGITS,
    MONEY_DIGITS,
    type StockFigures,
} from '../../domain/metals/stockSummary';

const { Text } = Typography;

interface FigureTile {
    key: string;
    labelKey: string;
    value: number | null;
    digits: number;
    unitKey: string;
}

const UNIT_GRAMS = 'metals.stockSummary.unit.grams';
const UNIT_RON = 'metals.stockSummary.unit.ron';
const UNIT_EUR = 'metals.stockSummary.unit.eur';
const UNIT_RON_PER_GRAM = 'metals.stockSummary.unit.ronPerGram';

const gramsTile = (key: string, labelKey: string, value: number | null): FigureTile => ({
    key,
    labelKey,
    value,
    digits: GRAMS_DIGITS,
    unitKey: UNIT_GRAMS,
});

const moneyTile = (
    key: string,
    labelKey: string,
    value: number | null,
    unitKey: string,
): FigureTile => ({
    key,
    labelKey,
    value,
    digits: MONEY_DIGITS,
    unitKey,
});

/** The weight tiles: gold in AU585 and AU999.99, any other metal in grams and fine grams. */
const weightTiles = (figures: StockFigures, kind: IncomingSummaryMetal): FigureTile[] =>
    figures.au585Quantity === null
        ? [
              gramsTile('quantity', 'metals.stockSummary.weight', figures.quantity),
              gramsTile('fine', `metals.stockSummary.fine.${kind}`, figures.purifiedQuantity),
          ]
        : [
              gramsTile('au585', 'metals.stockSummary.au585', figures.au585Quantity),
              gramsTile('au999', 'metals.stockSummary.au999', figures.purifiedQuantity),
          ];

const figureTiles = (
    figures: StockFigures,
    kind: IncomingSummaryMetal,
    pmpLabelKey: string,
): FigureTile[] => [
    ...weightTiles(figures, kind),
    { ...gramsTile('pmp', pmpLabelKey, figures.avgPrice), unitKey: UNIT_RON_PER_GRAM },
    moneyTile('ron', 'metals.stockSummary.worthRon', figures.valueRon, UNIT_RON),
    moneyTile('eur', 'metals.stockSummary.worthEur', figures.valueEur, UNIT_EUR),
];

export interface StockFiguresCardProps {
    t: (key: string) => string;
    testId: string;
    title: ReactNode;
    figures: StockFigures;
    kind: IncomingSummaryMetal;
    pmpLabelKey?: string;
    /** Shown under the figures, e.g. the bar splitting the whole stock by metal. */
    aside?: ReactNode;
}

/** The stock figures of one metal, or of the whole stock, as one compact line after a title. Not an antd Card: App.css forces 12px on every `.ant-card-body`. */
const StockFiguresCard = ({
    t,
    testId,
    title,
    figures,
    kind,
    pmpLabelKey = 'metals.pmp',
    aside,
}: StockFiguresCardProps) => {
    const { token } = theme.useToken();

    return (
        <div
            data-testid={testId}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                marginBottom: 6,
                padding: '4px 12px',
                background: token.colorFillAlter,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
            }}
        >
            <div
                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 20px' }}
            >
                <div style={{ minWidth: 70 }}>{title}</div>
                {figureTiles(figures, kind, pmpLabelKey).map(figure => (
                    <div
                        key={figure.key}
                        data-testid={`${testId}-${figure.key}`}
                        style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}
                    >
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {t(figure.labelKey)}
                        </Text>
                        <Text strong style={{ fontSize: 13 }}>
                            {figure.value === null
                                ? '–'
                                : formatStockNumber(figure.value, figure.digits)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {t(figure.unitKey)}
                        </Text>
                    </div>
                ))}
            </div>
            {aside}
        </div>
    );
};

export default StockFiguresCard;
